import {
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperTextProps,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import ClearIcon from '@mui/icons-material/Clear';
import { IconButton, Stack } from '@mui/material';
import { AxiosResponse } from 'axios';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOpenAlert, useShowLoader } from '../../Context/Layout.context';
import { UserPermission } from '../../enum/userPermissions';
import useSessionUser from '../../hooks/useSessionUser';
import {
  Folder,
  FolderMessageContent,
  foldersWithAllParticantsIncludedInReply,
  foldersWithHelpdesk,
  foldersWithNeedToInsertSendMessage,
  foldersWithSpecialCaseDataDisplaying,
  IMessageRecipientsList,
  IRecipientList,
  Message,
  MessageParticipant,
  MessageRecipients,
  Priority,
  RecipientTreeOption,
} from '../../models/communication.types';
import { defaultPageInfo, IPageInfo } from '../../models/page.types';
import { AssessmentSession } from '../../models/session.types';
import { AssementSessionData, Examiner, IExaminingTeamMemberWithFunctionId } from '../../models/taskGroup.types';
import { ExaminerFunction, getFunctionLabel } from '../../pages/Assessment/Assessment.types';
import { DashboardTile, SessionType } from '../../pages/Dashboard/Dashboard.types';
import { mailboxActions } from '../../reducers/mailboxReducer/mailboxSlice';
import {
  getSmsNotificationSetups,
  getSpecialCasesDefinitionsTitles,
} from '../../services/admin/settings/systemSettings.service';
import {
  sendForward,
  sendMessage,
  sendMessageAdmin,
  sendMessageAdminToAllChecked,
  sendMessageExamTaskGroupSolution,
  sendMessageHelpdesk,
  sendMessageSpecialCase,
  sendReply,
} from '../../services/communication/communication.service';
import { Examiner as ExaminerFromSolution, SelectedExaminer } from '../../services/examiners/examiners.types';
import {
  getAssessmentSessions,
  getRecipient,
  getRecipientAdmin,
  getUserStillInAssessmentSession,
} from '../../services/exams/assessmentSession/assessments.service';
import { hasPermission } from '../../services/users/identity/user.service';
import { SelectedUser } from '../../services/users/users.types';
import { RootState } from '../../store';
import { convertToFormatDateTime } from '../../utils/dateFunctionUtils';
import { formatBytes } from '../../utils/fileProcessing';
import { Examiner as ExaminerFromStructure, ExaminersList } from '../ExaminersStructure/ExaminersStructure.types';
import CancelEditingNewMessagePrompt from '../Mailbox/CancelEditingNewMessagePrompt.component';
import LinkBox from '../Mailbox/LinkBox.component';
import MessagePreviewBodyFooter from '../Mailbox/MessagePreviewBodyFooter/MessagePreviewBodyFooter.component';
import { TableMessageParticipant } from '../Mailbox/MessagesList/MessagesList.types';
import { getParticipantDisplayName, getTableParticipant } from '../Mailbox/MessagesList/utils';
import PriorityBox from '../Mailbox/PriorityBox/PriorityBox.component';
import { MessageActions } from '../MessageActions/MessageActions.component';
import { INotificationTypeId } from '../MessageActions/NotificationType.component';
import { Prompt } from '../Prompt/Prompt.component';
import { IndexedStrings } from '../SelectableTable/SelectableTable.types';
import { ISpecialCaseTitles } from '../SystemSettings/SpecialCases/SpecialCases.types';
import { ValidationRow } from '../ValidationRow/ValidationRow.components';
import { useStyles } from './NewMessage.styles';
import { useStyles as styles } from '../../pages/styles';

import { RecipientsAutocomplete } from './RecipientsAutocomplete.component';
import { RecipientsList } from './RecipientsList.component';
import { SmsNotificationSetupItem } from '../SystemSettings/SmsNotificationSetupManager/SmsNotificationSetupManager.component';
import { ExaminersType, mapExaminerFunctionToType } from '../../models/examiners.types';
import { useTranslation } from 'react-i18next';

interface NewMessageProps {
  onCancel?: () => void;
  onSend?: () => void;
  isModal?: boolean;
  recipient?: Examiner | SelectedUser[] | SelectedExaminer[] | ExaminerFromStructure;
  session?: AssementSessionData | DashboardTile | null;
  shouldSessionDataBeHide?: boolean;
  isAllPagesItemChecked?: boolean;
  countOfAllCheckedRecipients?: number;
  messageType?: 'standard' | 'helpdesk' | 'specialCase' | 'orgzMessage';
  recipientWithFunctionId?: ExaminersList;
  recipientWithFunctionIdFromExaminerTreemap?: IExaminingTeamMemberWithFunctionId;
  userFilters?: IndexedStrings;
  setCancelConfirmationOpen?: boolean;
  cancelConfirmationOpen?: (value: boolean) => void;
  rgzId?: string;
  cardId?: string;
  recipientFromOrgz?: ExaminerFromSolution;
}

const SUBJECT_MAX_LENGTH = 128;
const BODY_MAX_LENGTH = 4096;

const decodeHtmlEntities = (str: string) => {
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
};

const getForwardMessageBody = (oryginalMessage: FolderMessageContent) => {
  return `
------ Przekazywana wiadomość -------
Od: ${getParticipantDisplayName(oryginalMessage.sender)}
Data: ${convertToFormatDateTime(oryginalMessage.sentDate)}
Temat: ${decodeHtmlEntities(oryginalMessage.subject)}
Do: ${getParticipantDisplayName(oryginalMessage.recipient)}

${decodeHtmlEntities(oryginalMessage.body)}
`;
};

export const NewMessage: React.FC<NewMessageProps> = (props) => {
  const classes = useStyles();
  const mainClasses = styles();

  const openAlert = useOpenAlert();
  const showLoader = useShowLoader();
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const replyMessageId = useSelector((state: RootState) => state.mailbox.replyMessageId);
  const forwardMessageId = useSelector((state: RootState) => state.mailbox.forwardMessageId);
  const actionsHidden = replyMessageId || forwardMessageId;

  const loadedMessageContent = useSelector((state: RootState) => state.mailbox.loadedMessageContent);
  const activeFolder = useSelector((state: RootState) => state.mailbox.activeFolder);

  const isSpecialCaseForward = foldersWithSpecialCaseDataDisplaying.includes(activeFolder);

  const isHelpDeskFolder = foldersWithHelpdesk.includes(activeFolder);

  const [assessmentSessions, setAssessmentSessions] = useState<AssessmentSession[]>([]);
  const [selectedExamTaskGroupId, setSelectedExamTaskGroupId] = useState<string | undefined>(
    props.session ? (props.session.id ? props.session.id : props.session.assessmentSessionId) : '',
  );
  const [selectedSpecialCaseId, setSelectedSpecialCaseId] = useState<string>('');
  const [recipientsStructure, setRecipientsStructure] = useState<MessageRecipients>();
  const [selectedRecipients, setSelectedRecipients] = useState<RecipientTreeOption[]>([]);
  const [selectedRecipientsList, setSelectedRecipientsList] = useState<IRecipientList[]>([]);
  const [recipientsList, setRecipientsList] = useState<IRecipientList[]>([]);
  const [subject, setSubject] = useState<string>('');
  const [body, setBody] = useState<string>(
    loadedMessageContent && forwardMessageId ? getForwardMessageBody(loadedMessageContent) : '',
  );
  const [priority, setPriority] = useState<Priority>(Priority.Low);
  const [file, setFile] = useState<File | undefined>();
  const [link, setLink] = useState<string | undefined>();
  const [notificationId, setNotificationId] = useState<INotificationTypeId[]>([]);
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState<boolean>(false);
  const [isNoSessionMonit, setIsNoSessionMonit] = useState<boolean>(false);
  const [isNoSpecialCaseMonit, setIsNoSpecialCaseMonit] = useState<boolean>(false);
  const [atLeastOneRecipientIsStillInAssessmentSession, setAtLeastOneRecipientIsStillInAssessmentSession] =
    useState<boolean>(true);
  const [allOutdatedRecipientPromptOpened, setAllOutdatedRecipientPromptOpened] = useState<boolean>(false);
  const [multipleOutdatedRecipientsPromptOpened, setMultipleOutdatedRecipientsPromptOpened] = useState<boolean>(false);
  const [replyRecipients, setReplyRecipients] = useState<MessageParticipant[] | null>(null);
  const [selectedSessionExamTaskGroupId, setSelectedSessionExamTaskGroupId] = useState<string | undefined>();
  const [pageInfo, setPageInfo] = useState<IPageInfo>(defaultPageInfo);
  const [searchingValue, setSearchingValue] = useState<string>('');
  const [specialCaseTitles, setSpecialCaseTitles] = useState<ISpecialCaseTitles[]>();
  const [value, setValue] = useState<string>('');
  const [allChecked, setAllChecked] = useState<boolean>();
  const [isSmsEnabled, setIsSmsEnabled] = useState<boolean>(true);
  const [smsNotificationSetupItems, setSmsNotificationSetupItems] = useState<SmsNotificationSetupItem[]>([]);

  const { user, userAssessmentSessionInfo } = useSessionUser();
  const adminPermission = hasPermission(user, UserPermission.AdminPanelManage);

  const isHelpDeskMessage = props.messageType === 'helpdesk';
  const isSpecialCaseMessage = props.messageType === 'specialCase';
  const isOrgzMessage = props.messageType === 'orgzMessage';

  const isSubjectToLong = subject.length > SUBJECT_MAX_LENGTH;
  const isBodyToLong = body.length > BODY_MAX_LENGTH;
  const isSampleAssesmentSession = props.session && props.session.sessionType === SessionType.SampleAssessmentSession;

  const getIsSmsEnabled = () => {
    if (
      (recipientsStructure !== undefined &&
        (recipientsStructure.examinerFunction === ExaminerFunction.CZWO ||
          recipientsStructure.examinerFunction === ExaminerFunction.PZWO ||
          smsNotificationSetupItems.find(
            (w) => w.examinerFunctionId === mapExaminerFunctionToType(recipientsStructure.examinerFunction),
          )?.allowSendSmsNotification)) ||
      (userAssessmentSessionInfo.function &&
        smsNotificationSetupItems.find(
          (w) => w.examinerFunctionId === mapExaminerFunctionToType(userAssessmentSessionInfo.function),
        )?.allowSendSmsNotification)
    ) {
      setIsSmsEnabled(true);
    } else {
      setIsSmsEnabled(false);
    }
  };

  const getSessionData = () => {
    showLoader(true);
    getAssessmentSessions()
      .then((result: AssessmentSession[]) => {
        setAssessmentSessions(result);
        setIsNoSessionMonit(!adminPermission && !isHelpDeskMessage && result.length === 0);
        if ((forwardMessageId || replyMessageId) && loadedMessageContent) {
          setSelectedExamTaskGroupId(
            result.find(
              (assessmentSession) => assessmentSession.assessmentSessionId === loadedMessageContent.assessmentSession,
            )?.examTaskGroupId,
          );
        }
        showLoader(false);
      })
      .catch((error) => {
        showLoader(false);
        setIsNoSessionMonit(true);
        console.error(error);
      });
  };

  const getFilterValues = (filter: IndexedStrings) => {
    return Object.keys(filter).map((key) => {
      if (Array.isArray(filter[key])) {
        return (filter[key] as string[]).join(',');
      }
      return filter[key];
    });
  };

  const getFunctionId = (): string | undefined => {
    if (props.recipientWithFunctionIdFromExaminerTreemap) {
      return [
        props.recipientWithFunctionIdFromExaminerTreemap.sampleAssessmentChairmanId,
        props.recipientWithFunctionIdFromExaminerTreemap.sampleAssessmentMemberId,
        props.recipientWithFunctionIdFromExaminerTreemap.taskGroupCoordinatorId,
        props.recipientWithFunctionIdFromExaminerTreemap.teamChairmanId,
        props.recipientWithFunctionIdFromExaminerTreemap.teamMemberId,
      ].find((id) => id);
    }
  };

  const getSpecialCaseDefinitions = () => {
    showLoader(true);
    getSpecialCasesDefinitionsTitles()
      .then((result: ISpecialCaseTitles[]) => {
        setSpecialCaseTitles(result);
        setIsNoSpecialCaseMonit(isSpecialCaseMessage && result.length === 0);
        showLoader(false);
      })
      .catch((error) => {
        console.error(error); //TODO Add Monit when is no recipients assigned
        setIsNoSpecialCaseMonit(true);
        showLoader(false);
      });
  };

  const getSmsNotificationSetupData = () => {
    getSmsNotificationSetups().then(
      (result: SmsNotificationSetupItem[]) => {
        setSmsNotificationSetupItems(result);
      },
      () => {
        openAlert('error');
      },
    );
  };

  useEffect(() => {
    if (!adminPermission && !props.session && !isSpecialCaseMessage) {
      getSessionData();
    }
    getSmsNotificationSetupData();
  }, []);

  useEffect(() => {
    isSpecialCaseMessage && getSpecialCaseDefinitions();
  }, []);

  useEffect(() => {
    smsNotificationSetupItems?.length > 0 &&
      (recipientsStructure || userAssessmentSessionInfo.function) &&
      getIsSmsEnabled();
  }, [smsNotificationSetupItems, recipientsStructure, userAssessmentSessionInfo.function]);

  const handleGetRecipient = (zwo?: boolean) => {
    showLoader(true);
    getRecipient(selectedExamTaskGroupId, zwo)
      .then((result: MessageRecipients) => {
        setRecipientsStructure(result);
        showLoader(false);
      })
      .catch((error) => {
        console.error(error); //TODO Add Monit when is no recipients assigned
        showLoader(false);
      });
  };

  const selectedSession = assessmentSessions.find((session) => session.examTaskGroupId === selectedExamTaskGroupId);
  const selectedSessionFunctionIds: ExaminerFunction[] | undefined = selectedSession?.functionsInSession;

  const selectedSessionId = selectedSession?.assessmentSessionId || props.session?.assessmentSessionId;

  const isPZWO: boolean =
    selectedSessionFunctionIds !== undefined && selectedSessionFunctionIds.includes(ExaminerFunction.PZWO);

  const isCZE: boolean =
    selectedSessionFunctionIds !== undefined && selectedSessionFunctionIds.includes(ExaminerFunction.CZE);

  const isCZWO: boolean =
    selectedSessionFunctionIds !== undefined && selectedSessionFunctionIds.includes(ExaminerFunction.CZWO);

  const isZWO: boolean = isCZWO || isPZWO;

  const hasTwoFunctions: boolean =
    selectedSession?.functionsInSession !== undefined && selectedSession?.functionsInSession.length >= 2;

  const KKisZWO: boolean | undefined =
    selectedSession !== undefined && selectedSession.hasZwo !== null ? selectedSession.hasZwo : false;

  useEffect(() => {
    if (!adminPermission) {
      if (selectedExamTaskGroupId !== '' && !props.session && !isHelpDeskMessage && !isOrgzMessage) {
        setSelectedSessionExamTaskGroupId(selectedSession?.examTaskGroupId);
        if (!KKisZWO && !hasTwoFunctions && !isZWO) {
          handleGetRecipient();
        } else if (isZWO && !hasTwoFunctions) {
          handleGetRecipient(true);
        }
      }
    } else {
      if (
        !props.recipient &&
        !props.recipientWithFunctionId &&
        !props.recipientWithFunctionIdFromExaminerTreemap &&
        !props.recipientFromOrgz &&
        !replyMessageId
      ) {
        if (searchingValue.length < 2) return;
        showLoader(true);
        getRecipientAdmin(pageInfo.currentPage, pageInfo.pageSize, searchingValue)
          .then((result: IMessageRecipientsList) => {
            setRecipientsList(result.userList);
            setPageInfo(result.pageInfo);
          })
          .catch((error) => {
            console.error(error); //TODO Add Monit when is no recipients assigned
          })
          .finally(() => showLoader(false));
      }
    }
  }, [selectedExamTaskGroupId, pageInfo.currentPage, searchingValue, allChecked]);

  const handleAssessmentSessionChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    setSelectedExamTaskGroupId(event.target.value as string);
    setSelectedRecipients([]);
    setRecipientsStructure(undefined);
    setValue('');
  };

  const handleSpecialCaseChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    setSelectedSpecialCaseId(event.target.value as string);
  };

  const handleSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSubject(event.target.value);
  };

  const handleBodyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBody(event.target.value);
  };

  const handleSendSuccess = (sentMessage: Message) => {
    openAlert('success', 'Wiadomość została wysłana');
    props.onSend && props.onSend();
    if (
      (activeFolder !== Folder.HelpdeskReported &&
        activeFolder !== Folder.HelpdeskSolved &&
        activeFolder !== Folder.Received) ||
      (props.messageType === 'helpdesk' && foldersWithNeedToInsertSendMessage.includes(activeFolder))
    ) {
      dispatch(
        mailboxActions.addMessagesToInsert({
          messages: [
            {
              folder: activeFolder,
              message: sentMessage,
            },
          ],
          userId: user.profile.sub,
        }),
      );
    }
  };

  useEffect(() => {
    if (!loadedMessageContent || !replyMessageId) {
      return;
    }
    if (foldersWithAllParticantsIncludedInReply.includes(activeFolder)) {
      setReplyRecipients(
        loadedMessageContent.recipient
          .concat(loadedMessageContent.sender)
          .filter((item) => item.userId !== user.profile.sub),
      );
    } else {
      const participantInfo = getTableParticipant(activeFolder, user, loadedMessageContent);
      if (participantInfo === TableMessageParticipant.recipient) {
        setReplyRecipients(loadedMessageContent.recipient);
      } else {
        setReplyRecipients([loadedMessageContent.sender]);
      }
    }
  }, [activeFolder, user, loadedMessageContent, replyMessageId]);

  const handleSendError = () => {
    openAlert('error', 'Wysyłanie wiadomości nie powiodło się. Spróbuj ponownie później');
  };

  const functionId = getFunctionId();

  const getRecipientsForMessage = (): (string | undefined)[] => {
    return props.recipientWithFunctionId || props.recipientWithFunctionIdFromExaminerTreemap
      ? props.recipientWithFunctionId !== undefined
        ? isSampleAssesmentSession
          ? [props.recipientWithFunctionId.examinerFunctionId]
          : [props.recipientWithFunctionId.id]
        : '' || props.recipientWithFunctionIdFromExaminerTreemap
        ? [functionId !== undefined ? functionId : '']
        : ['']
      : selectedRecipients.map((selectedRecipient) => {
          return selectedRecipient.functionId;
        });
  };

  const getRecipientsForAdminMessage = (): string[] => {
    return !isHelpDeskMessage
      ? props.recipient
        ? Array.isArray(props.recipient)
          ? props.recipient.map((item) => (item.id ? item.id : item.userId ? item.userId : '????'))
          : [
              props.recipient.userId || props.recipient.id
                ? props.recipient.userId || props.recipient.id || '????'
                : props.recipient.examinerId || '????',
            ]
        : selectedRecipientsList.map((selectedRecipient) => {
            return selectedRecipient.userId;
          })
      : ['helpdesk'];
  };

  const getRecipientsForForwardMessage = (): string[] => {
    if (adminPermission) {
      return getRecipientsForAdminMessage();
    } else {
      return selectedRecipients.map((selectedRecipient) => {
        return selectedRecipient.userId;
      });
    }
  };

  const isNotActiveHelpdeskFolder = () => {
    return (
      (activeFolder !== Folder.HelpdeskReported && activeFolder !== Folder.HelpdeskSolved) ||
      props.messageType === 'helpdesk'
    );
  };

  const handleSend = () => {
    showLoader(true, 'Wysyłanie wiadomości');
    sendMessage(
      subject,
      body,
      selectedSessionId,
      priority,
      file,
      link,
      getRecipientsForMessage(),
      notificationId,
      (activeFolder !== Folder.HelpdeskReported && activeFolder !== Folder.HelpdeskSolved) ||
        props.messageType === 'helpdesk'
        ? foldersWithNeedToInsertSendMessage.includes(activeFolder)
        : false,
    )
      .then(handleSendSuccess)
      .catch(handleSendError)
      .finally(() => showLoader(false));
  };

  const handleSendAdmin = () => {
    showLoader(true, 'Wysyłanie wiadomości');
    sendMessageAdmin(
      subject,
      body,
      priority,
      file,
      link,
      getRecipientsForAdminMessage().length
        ? getRecipientsForAdminMessage()
        : [props.recipientWithFunctionIdFromExaminerTreemap?.examiner?.examinerId],
      notificationId,
      isNotActiveHelpdeskFolder() ? foldersWithNeedToInsertSendMessage.includes(activeFolder) : false,
    )
      .then(handleSendSuccess)
      .catch(handleSendError)
      .finally(() => showLoader(false));
  };

  const sendMessageAdminToAll = () => {
    const filters = getFilterValues(props.userFilters !== undefined ? props.userFilters : {});
    showLoader(true, 'Wysyłanie wiadomości');
    sendMessageAdminToAllChecked(subject, body, priority, file, link, notificationId, ...(filters as string[]))
      .then(handleSendSuccess)
      .catch(handleSendError)
      .finally(() => showLoader(false));
  };

  const handleSendReply = (messageId: string) => {
    showLoader(true, 'Wysyłanie wiadomości');
    sendReply(
      messageId,
      body,
      priority,
      file,
      link,
      notificationId,
      isNotActiveHelpdeskFolder() ? foldersWithNeedToInsertSendMessage.includes(activeFolder) : false,
    )
      .then(handleSendSuccess)
      .catch(handleSendError)
      .finally(() => showLoader(false));
  };

  const handleSendForward = (messageId: string) => {
    showLoader(true, 'Wysyłanie wiadomości');
    sendForward(
      messageId,
      body,
      priority,
      getRecipientsForForwardMessage(),
      notificationId,
      isNotActiveHelpdeskFolder() ? foldersWithNeedToInsertSendMessage.includes(activeFolder) : false,
    )
      .then(handleSendSuccess)
      .catch(handleSendError)
      .finally(() => showLoader(false));
  };

  const handleSendHelpdesk = () => {
    showLoader(true, 'Wysyłanie wiadomości');
    sendMessageHelpdesk(
      subject,
      body,
      selectedExamTaskGroupId,
      priority,
      file,
      link,
      foldersWithNeedToInsertSendMessage.includes(activeFolder),
    )
      .then(handleSendSuccess)
      .catch(handleSendError)
      .finally(() => showLoader(false));
  };

  const handleSendSpecialCase = () => {
    showLoader(true, 'Wysyłanie wiadomości');
    sendMessageSpecialCase(body, props.rgzId, selectedSpecialCaseId, file, link, notificationId)
      .then(
        (res) => {
          handleSendSuccess(res);
        },
        (err) => {
          console.log(err);
          handleSendError();
        },
      )
      .finally(() => showLoader(false));
  };

  const handleSendExamTaskGroupSolution = () => {
    showLoader(true, 'Wysyłanie wiadomości');
    sendMessageExamTaskGroupSolution(body, subject, priority, props.rgzId, file, link, notificationId)
      .then(handleSendSuccess)
      .catch(handleSendError)
      .finally(() => showLoader(false));
  };

  const handleSendMail = () => {
    if (replyMessageId) {
      handleSendReply(replyMessageId);
    } else if (forwardMessageId) {
      handleSendForward(forwardMessageId);
    } else if (
      adminPermission &&
      !props.isAllPagesItemChecked &&
      !isHelpDeskMessage &&
      !isSpecialCaseMessage &&
      !allChecked
    ) {
      handleSendAdmin();
    } else if (props.isAllPagesItemChecked || allChecked) {
      sendMessageAdminToAll();
    } else if (isHelpDeskMessage) {
      handleSendHelpdesk();
    } else if (isSpecialCaseMessage) {
      handleSendSpecialCase();
    } else if (isOrgzMessage) {
      handleSendExamTaskGroupSolution();
    } else {
      handleSend();
    }
  };

  const handlePriorityChange = (priority: Priority) => {
    setPriority(priority);
  };

  const hadleRemoveFile = () => {
    setFile(undefined);
  };

  const handleRemoveLink = () => {
    setLink(undefined);
  };

  const getExaminerName = () => {
    if (props.recipientWithFunctionId !== undefined) {
      return props.recipientWithFunctionId?.examiner
        ? props.recipientWithFunctionId?.examiner.displayName
        : `(${getFunctionLabel(props.recipientWithFunctionId?.function)})`;
    }
    if (props.recipientWithFunctionIdFromExaminerTreemap && props.recipientWithFunctionIdFromExaminerTreemap.examiner) {
      return `${props.recipientWithFunctionIdFromExaminerTreemap.examiner.firstName} ${props.recipientWithFunctionIdFromExaminerTreemap.examiner.lastName}`;
    }
  };

  const getSingleRecipient = () => {
    if (props.recipient && !Array.isArray(props.recipient)) {
      return `${props.recipient.firstName}  ${props.recipient.lastName}`;
    }
    if (props.recipientFromOrgz) {
      return `(E) ${props.recipientFromOrgz.firstName}  ${props.recipientFromOrgz.lastName}`;
    }
  };

  const handleCancel = () => {
    setCancelConfirmationOpen(true);
  };

  const handleCancelConfirmation = () => {
    setSelectedExamTaskGroupId('');
    setSelectedRecipients([]);
    setSelectedRecipientsList([]);
    setSubject('');
    setBody('');
    setPriority(Priority.Low);
    setFile(undefined);
    setLink(undefined);
    setNotificationId([]);
    setCancelConfirmationOpen(false);
    props.cancelConfirmationOpen !== undefined ? props.cancelConfirmationOpen(false) : '';
    props.onCancel && props.onCancel();
  };
  useEffect(() => {
    if (isOrgzMessage) {
      if (smsNotificationSetupItems.find((w) => w.examinerFunctionId === ExaminersType.PZE)?.allowSendSmsNotification) {
        setIsSmsEnabled(true);
      } else {
        setIsSmsEnabled(false);
      }
    }
    if (isSpecialCaseMessage) {
      if (smsNotificationSetupItems.find((w) => w.examinerFunctionId === ExaminersType.CZE)?.allowSendSmsNotification) {
        setIsSmsEnabled(true);
      } else {
        setIsSmsEnabled(false);
      }
    }
  }, [smsNotificationSetupItems]);

  useEffect(() => {
    if (
      replyMessageId &&
      loadedMessageContent &&
      loadedMessageContent.assessmentSession &&
      replyRecipients &&
      !isHelpDeskFolder
    ) {
      const uniqueRecipients = Array.from(new Set(replyRecipients.map((item) => item.userId)));
      getUserStillInAssessmentSession(loadedMessageContent.assessmentSession, uniqueRecipients).then(
        (response: AxiosResponse<string[]>) => {
          setAtLeastOneRecipientIsStillInAssessmentSession(response.data.length > 0);
          if (!response.data.length) {
            setAllOutdatedRecipientPromptOpened(true);
          } else if (
            replyRecipients.length > 1 &&
            response.data.length > 0 &&
            response.data.length < replyRecipients.length
          ) {
            setMultipleOutdatedRecipientsPromptOpened(true);
          }
        },
      );
    }
  }, [replyMessageId, loadedMessageContent, replyRecipients]);

  const sessionIsNotChoosenInHelpDesk =
    isHelpDeskMessage && assessmentSessions.length > 0 && selectedExamTaskGroupId === '';

  const isSendButtonDisabled =
    (!isHelpDeskFolder && !atLeastOneRecipientIsStillInAssessmentSession) ||
    isBodyToLong ||
    isSubjectToLong ||
    !body ||
    (forwardMessageId && getRecipientsForForwardMessage().length === 0 && !allChecked) ||
    (!isHelpDeskMessage &&
      !isHelpDeskFolder &&
      !adminPermission &&
      !isSpecialCaseMessage &&
      !replyMessageId &&
      !forwardMessageId &&
      !isOrgzMessage &&
      ((!allChecked && adminPermission && getRecipientsForAdminMessage().length === 0) ||
        (!adminPermission && !isOrgzMessage && getRecipientsForMessage().length === 0) ||
        !subject)) ||
    (!isHelpDeskFolder && sessionIsNotChoosenInHelpDesk) ||
    (isSpecialCaseMessage && selectedSpecialCaseId === '') ||
    (!selectedRecipientsList.length &&
      !selectedRecipients.length &&
      !replyMessageId &&
      !isSpecialCaseMessage &&
      (!body || (!subject && !allChecked))) ||
    (subject === '' && !isSpecialCaseMessage && !forwardMessageId && !replyMessageId);

  const canSendHightPriority =
    selectedSessionFunctionIds?.includes(ExaminerFunction.KK) ||
    selectedSessionFunctionIds?.includes(ExaminerFunction.KGZ);

  const canSendMediumPriority = canSendHightPriority || selectedSessionFunctionIds?.includes(ExaminerFunction.PZE);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue((event.target as HTMLInputElement).value);
    setSelectedRecipients([]);
  };

  const radioButonsValue = {
    session: { value: 'Session', label: 'Sesja Egzaminacyjna' },
    zwo: { value: 'Zwo', label: 'ZWO' },
  };

  useEffect(() => {
    if (value === radioButonsValue.session.value) {
      handleGetRecipient();
    }
    if (value === radioButonsValue.zwo.value) {
      handleGetRecipient(true);
    }
  }, [value, searchingValue]);

  const handleOnScroll = useCallback(
    (endReached: boolean) => {
      if (endReached && pageInfo !== undefined && pageInfo.hasNext) {
        setPageInfo((prevState) => {
          return { ...prevState, currentPage: prevState.currentPage + 1 };
        });
      }
    },
    [pageInfo.hasNext],
  );

  const handleSearchingValueChange = (value: string) => {
    setSearchingValue(value);
    setPageInfo(defaultPageInfo);
    setRecipientsList([]);
  };

  return (
    <>
      <FormGroup>
        {((!isSpecialCaseMessage && !adminPermission && !actionsHidden && !isOrgzMessage) ||
          (!isHelpDeskMessage && assessmentSessions.length > 0 && !isOrgzMessage)) &&
          !replyMessageId &&
          !forwardMessageId && (
            <FormControlLabel
              label="Sesja:"
              labelPlacement="start"
              className={classes.formControlContainer}
              control={
                <FormControl fullWidth style={{ margin: '10px 0 10px 10px' }}>
                  {props.session ? (
                    <TextField
                      id="standard-read-only-input"
                      value={`${props.session.name ? props.session.name : props.session.assessmentSessionName} ${
                        props.session.examTaskGroupName ? '/ ' + props.session.examTaskGroupName : ''
                      }`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="standard"
                    />
                  ) : (
                    <>
                      <InputLabel id="session-select-label" style={{ marginLeft: '15px' }}>
                        Wybierz z listy
                      </InputLabel>
                      <Select
                        labelId="session-select-label"
                        label="Wybierz z listy"
                        variant="outlined"
                        onChange={handleAssessmentSessionChange}
                        value={selectedExamTaskGroupId}
                      >
                        {assessmentSessions.map((session) => (
                          <MenuItem disableRipple key={session.assessmentSessionId} value={session.examTaskGroupId}>
                            {session.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  )}
                </FormControl>
              }
            />
          )}
        {isSpecialCaseMessage && specialCaseTitles && (
          <FormControlLabel
            label="Rodzaj:"
            labelPlacement="start"
            className={classes.formControlContainer}
            control={
              <FormControl variant="outlined" fullWidth style={{ margin: '10px 0 10px 10px' }}>
                <>
                  <InputLabel id="specialCase-select-label">Wybierz z listy</InputLabel>
                  <Select
                    labelId="specialCase-select-label"
                    label="Wybierz z listy"
                    variant="outlined"
                    onChange={handleSpecialCaseChange}
                    value={selectedSpecialCaseId}
                  >
                    {specialCaseTitles.map((item) => (
                      <MenuItem disableRipple key={item.specialCaseId} value={item.specialCaseId}>
                        {item.title}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              </FormControl>
            }
          />
        )}
        {(hasTwoFunctions || KKisZWO) &&
          !isHelpDeskMessage &&
          !replyMessageId &&
          !forwardMessageId &&
          !isSpecialCaseForward && (
            <FormControl style={{ display: 'flex', flexDirection: 'row', marginTop: '10px' }}>
              <FormLabel
                style={{ marginRight: '20px', color: 'rgba(0, 0, 0, 0.87)' }}
                id="demo-controlled-radio-buttons-group"
              >
                Do:
              </FormLabel>
              <RadioGroup
                style={{ display: 'flex', flexDirection: 'row' }}
                aria-label="do"
                name="radio-buttons"
                value={value}
                onChange={handleChange}
              >
                <FormControlLabel
                  value={radioButonsValue.zwo.value}
                  control={<Radio color="primary" />}
                  label={radioButonsValue.zwo.label}
                />
                <FormControlLabel
                  value={radioButonsValue.session.value}
                  control={<Radio color="primary" />}
                  label={radioButonsValue.session.label}
                />
              </RadioGroup>
            </FormControl>
          )}
        {!isSpecialCaseMessage &&
          ((!adminPermission && recipientsStructure !== undefined) ||
            (!adminPermission && getRecipientsForMessage().length !== 0) ||
            (!adminPermission && getSingleRecipient()?.length !== 0) ||
            adminPermission ||
            replyMessageId ||
            forwardMessageId ||
            isSpecialCaseForward ||
            (!adminPermission && (hasTwoFunctions || KKisZWO) && value !== '')) && (
            <>
              {replyMessageId && loadedMessageContent && replyRecipients ? (
                <div className={classes.formControlContainer} style={{ padding: '10px 0 15px 0' }}>
                  <Typography className={classes.replyRecipient}>
                    Do: {getParticipantDisplayName(replyRecipients)}
                  </Typography>
                </div>
              ) : (
                <FormControlLabel
                  label={!hasTwoFunctions ? 'Do:' : ''}
                  labelPlacement="start"
                  className={classes.formControlContainer}
                  control={
                    <FormControl fullWidth style={{ margin: '10px 0 10px 10px' }}>
                      {props.recipientWithFunctionIdFromExaminerTreemap ||
                      props.recipientWithFunctionId ||
                      props.recipient ||
                      props.recipientFromOrgz ||
                      isHelpDeskMessage ? (
                        <TextField
                          id="session-read-only-input"
                          value={
                            Array.isArray(props.recipient)
                              ? props.recipient.length < 20
                                ? props.recipient.map((item) => ` ${item.firstName}  ${item.lastName}`)
                                : `Wybrano ${
                                    !props.isAllPagesItemChecked
                                      ? props.recipient.length
                                      : props.countOfAllCheckedRecipients
                                  } adresatów`
                              : isHelpDeskMessage
                              ? `Helpdesk`
                              : props.recipient || props.recipientFromOrgz
                              ? getSingleRecipient()
                              : getExaminerName()
                          }
                          InputProps={{
                            readOnly: true,
                          }}
                          variant="standard"
                          multiline
                        />
                      ) : !adminPermission ? (
                        <RecipientsAutocomplete
                          recipientsStructure={recipientsStructure}
                          selectedRecipients={selectedRecipients}
                          onChange={setSelectedRecipients}
                          isCZE={isCZE}
                          isCZWO={isCZWO}
                        />
                      ) : (
                        <RecipientsList
                          onRecipientsChange={setSelectedRecipientsList}
                          selectedRecipientsList={selectedRecipientsList}
                          recipients={recipientsList}
                          onScroll={handleOnScroll}
                          onInputChange={handleSearchingValueChange}
                          pageInfo={pageInfo}
                          allChecked={setAllChecked}
                        />
                      )}
                    </FormControl>
                  }
                />
              )}
            </>
          )}
        {!actionsHidden && (
          <FormControlLabel
            label="Temat:"
            labelPlacement="start"
            className={classes.formControlContainer}
            control={
              <FormControl fullWidth style={{ paddingLeft: '10px' }} className={classes.subjectInput}>
                <TextField
                  value={isSpecialCaseMessage ? `Szczególny przypadek: ID:${props.rgzId}` : subject}
                  disabled={isSpecialCaseMessage}
                  onChange={handleSubjectChange}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.stopPropagation();
                    }
                  }}
                  error={isSubjectToLong}
                  FormHelperTextProps={
                    {
                      component: 'div',
                    } as Partial<FormHelperTextProps<'p'>>
                  }
                  helperText={
                    <ValidationRow
                      helperText={isSubjectToLong ? 'Podany temat jest za długi' : ''}
                      limitsVisible={isSubjectToLong}
                      valueLength={subject.length}
                      limit={SUBJECT_MAX_LENGTH}
                      withError={isSubjectToLong}
                    />
                  }
                />
              </FormControl>
            }
          />
        )}
        <PriorityBox priority={priority} />

        <FormControl
          fullWidth
          className={classes.formControlContainer}
          style={{ marginTop: '10px', marginBottom: '10px', border: 'unset', overflow: 'auto' }}
        >
          <FormLabel className={mainClasses.visuallyHidden} htmlFor="message-content">
            Treść wiadomości
          </FormLabel>
          <TextField
            id="message-content"
            style={{ minHeight: 'calc( 100vh - 800px)', maxHeight: '400px' }}
            multiline
            minRows={!!props.isModal ? 5 : 15}
            autoFocus
            focused
            value={body}
            onChange={handleBodyChange}
            onKeyDown={(e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.stopPropagation();
              }
            }}
            error={isBodyToLong}
            aria-label="treść wiadomości"
            aria-hidden="false"
            FormHelperTextProps={
              {
                component: 'div',
              } as Partial<FormHelperTextProps<'p'>>
            }
            helperText={
              <ValidationRow
                helperText={isBodyToLong ? 'Podany tekst jest za długi' : ''}
                limitsVisible={isBodyToLong}
                valueLength={body.length}
                limit={BODY_MAX_LENGTH}
                withError={isBodyToLong}
              />
            }
          />
        </FormControl>
      </FormGroup>

      {forwardMessageId && loadedMessageContent ? (
        <MessagePreviewBodyFooter messageContent={loadedMessageContent} />
      ) : (
        <>
          {(file || link) && (
            <div className={classes.attachmentsContainer}>
              {file && (
                <div className={classes.attachmentContainer}>
                  <span style={{ fontWeight: 600, paddingRight: '5px' }}>Załącznik: </span>
                  <div style={{ border: '1px solid #ced4da', borderRadius: '3px', padding: '5px' }}>
                    <span style={{ fontWeight: 600 }}>{file.name} </span> ({formatBytes(file.size)})
                    <>
                      <IconButton disableRipple onClick={hadleRemoveFile} style={{ padding: '3px' }}>
                        <ClearIcon style={{ fontSize: '0.8em' }} />
                      </IconButton>
                    </>
                  </div>
                </div>
              )}
              {link && (
                <div className={classes.attachmentContainer}>
                  <span style={{ fontWeight: 600, paddingRight: '5px' }}>Link: </span>
                  <LinkBox url={link} onRemoveLink={handleRemoveLink} />
                </div>
              )}
            </div>
          )}
        </>
      )}

      <Stack direction="row" spacing={2} style={{ paddingBottom: '5px' }}>
        <Button variant="outlined" color="primary" onClick={handleCancel}>
          Odrzuć
        </Button>
        <Button variant="contained" color="primary" disabled={isSendButtonDisabled} onClick={handleSendMail}>
          Wyślij
        </Button>
        <MessageActions
          priority={priority}
          canSendHightPriority={canSendHightPriority}
          canSendMediumPriority={canSendMediumPriority}
          onPriorityChange={handlePriorityChange}
          onFileChanged={forwardMessageId ? undefined : setFile}
          onLinkChanged={forwardMessageId ? undefined : setLink}
          link={link}
          notificationId={setNotificationId}
          isHelpDesk={isHelpDeskMessage}
          isSpecialCaseMessage={isSpecialCaseMessage}
          isSmsEnabled={isSmsEnabled}
        />
      </Stack>

      <CancelEditingNewMessagePrompt
        open={
          cancelConfirmationOpen ||
          (props.setCancelConfirmationOpen !== undefined ? props.setCancelConfirmationOpen : false)
        }
        onConfirm={handleCancelConfirmation}
        onCancel={() => {
          setCancelConfirmationOpen(false);
          props.cancelConfirmationOpen && props.cancelConfirmationOpen(false);
        }}
      />
      <Prompt
        type="error"
        open={isNoSessionMonit}
        mainText={
          'Nie możesz wysłać wiadomości, ponieważ nie posiadasz w tej chwili żadnej przypisanej sesji egzaminacyjnej'
        }
        isBold={true}
        onConfirm={() => {
          setIsNoSessionMonit(false);
          props.onCancel && props.onCancel();
        }}
      />
      {allOutdatedRecipientPromptOpened && (
        <Prompt
          type="error"
          mainText={
            'Nie można odpowiedzieć na wiadomość, ponieważ osoba, do której chcesz wysłać wiadomość, nie pełni już funkcji w sesji oceniania'
          }
          isBold={true}
          onConfirm={() => setAllOutdatedRecipientPromptOpened(false)}
        />
      )}
      {multipleOutdatedRecipientsPromptOpened && (
        <Prompt
          type="confirmation"
          mainText={`Nie wszyscy adresaci z listy pełnią wciąż funkcję w systemie ${t(
            'global:shortNames.system',
          )}. Czy mimo to chcesz odpowiedzieć na wiadomość?`}
          mainActionButtonText="TAK"
          isBold={true}
          onConfirm={() => setMultipleOutdatedRecipientsPromptOpened(false)}
          onCancel={handleCancelConfirmation}
        />
      )}
      <Prompt
        type="error"
        open={isNoSpecialCaseMonit}
        mainText={
          'Nie można zgłosić szczególnego przypadku, ponieważ w systemie nie zostały zdefiniowane jego żadne rodzaje.'
        }
        isBold={true}
        onConfirm={() => {
          setIsNoSpecialCaseMonit(false);
          props.onCancel !== undefined ? props.onCancel() : '';
        }}
      />
    </>
  );
};
